import { deriveAnchoredAge } from '../utils/levelCalculations';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { FaUser, FaList, FaRunning } from 'react-icons/fa';
import { DIMENSION_STYLES, UnifiedHeader, UnifiedLegend } from '../styles/DimensionStyles';
import { calculateFormProgress } from '../utils/gradeCalculationForWithdrawal';

// Definimos las interfaces localmente
interface Skill {
  id: number;
  name: string;
  levels: string[];
  selectedLevelIndex: number | null;
}

interface Dimension {
  id: number;
  name: string;
  skills: Skill[];
}

interface SkillLevel {
  dimensionId: number;
  skillId: number;
  level: number | null;
  notEvaluated?: boolean;
}

interface Student {
  id: number | string;
  name: string;
  course: string;
  birthDate: string;
  skillLevels: SkillLevel[];
  previousYearLevels?: {
    dimensionId: number;
    skillId: number;
    level: number | null;
    year: string;
  }[];
  qualitativeDescription?: string;
}

const LEVEL_COLORS = {
  expected: 'bg-yellow-50 border-2 border-yellow-400',    // Color amarillo claro para nivel esperado
  previous: 'bg-gray-50 border-2 border-gray-400',        // Color gris claro para nivel del año anterior
  selected: 'bg-blue-500 text-white shadow-md',           // Color azul para nivel seleccionado
  default: 'bg-white hover:bg-blue-50 border border-gray-200', // Color por defecto
  notApplicable: 'bg-gray-50 text-gray-400 italic',      // Color para "No aplica" no seleccionado
  notApplicableSelected: 'bg-blue-500 text-white shadow-md italic', // Color para "No aplica" seleccionado
  previouslySelected: 'bg-blue-50 border border-blue-300', // Color para nivel previamente seleccionado
};

// Datos de fallback para Corporalidad y Movimiento
// Habilidades Corporalidad y Movimiento (hardcodeadas)
const corporalidadMovimientoSkills: Skill[] = [
  {
    "id": 1,
          "name": "Manipulación y control de objetos",
    "levels": [
      "No Aplica",
            "Controla la fuerza que necesita ejercer sobre un elemento para lograr su objetivo; por ejemplo, arrastrar una caja con materiales de un lugar a otro, lanzar fuerte o suave una pelota para encestarla, patear una pelota con fuerza o suavidad hacia una dirección determinada, al empujar un objeto en un plano inclinado, entre otras.",
            "Ejecuta las posturas y movimientos que requiere para lograr sus objetivos al realizar acciones, como trepar sogas o escaleras, tirar la cuerda, entre otras.",
            "Lanzan objetos de diferentes formas y tamaños con una o ambas manos.",
            "Botean un balón con una mano y luego con la otra en una distancia determinada.",
            "Hacen rodar un balón con precisión hacia un punto determinado.",
            "Utilizan formas maduras de manipulación para el control de objetos; por ejemplo: lanzan según el objetivo; atrapan con seguridad, amortiguando la recepción; golpean en el aire una pelota con la mano, el pie o un implemento.",
            "Aplican habilidades específicas de manipulación en juegos deportivos; por ejemplo: reciben eficazmente implementos en situaciones de juego, lanzan oportunamente de acuerdo a la situación, golpean un objeto utilizando el cuerpo o un implemento.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 2,
          "name": "Equilibrio",
          "levels": [
      "No Aplica",
            "Mantiene el equilibrio al desplazarse siguiendo líneas rectas, curvas, zigzag o laberintos, en juegos o situaciones cotidianas.",
            "Mantiene el equilibrio cuando cambia de dirección (hacia adelante, hacia atrás y de costado) al caminar, saltar y correr.",
            "Mantienen el equilibrio con uno o ambos pies en superficies con variadas formas y tamaños, conservando el control del cuerpo.",
            "Mantienen el equilibrio en punta de pie, por períodos breves.",
            "Mantiene el equilibrio de un balón en una mano mientras se desplazan sobre una viga.",
            "Mantienen el control de su cuerpo en superficies pequeñas; por ejemplo: sobre una cuerda ubicada a unos cincuenta centímetros del suelo.",
            "Mantienen el equilibrio durante lanzamientos, saltos o golpes a implementos.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 3,
          "name": "Locomoción",
          "levels": [
      "No Aplica",
      "No Aplica",
            "No Aplica",
            "Se desplazan por medio de la carrera en diferentes distancias y direcciones con una correcta alternancia de piernas y brazos; por ejemplo: hacia adelante, atrás, izquierda y derecha.",
            "Se desplazan en sentido horizontal y vertical de forma coordinada y manteniendo el control de su cuerpo; por ejemplo: carreras, saltos, entre otros.",
            "Combinan y usan el máximo de habilidades de desplazamiento, como reptar y correr de forma coordinada, trepar y saltar, caminar, correr y saltar, deslizar y saltar en un pie, saltar con un pie y luego con el otro.",
            "Utilizan patrones locomotores en su fase madura; por ejemplo: corren en diferentes direcciones y saltan obstáculos con seguridad; lanzan y atrapan en carrera.",
            "Usan habilidades de locomoción en juegos deportivos; por ejemplo: corren en forma segura y coordinada hacia diferentes direcciones en busca del implemento deportivo.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 4,
          "name": "Giros y volteos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Voltean hacia adelante y atrás, manteniendo el control del cuerpo.",
            "Ejecutan volteos y giros en forma individual y con la ayuda de un compañero.",
            "Ejecutan la posición invertida. Ejecutan giros en 180 grados después de haber realizado una carrera.",
            "No Aplica",
            "Explican cómo se ejecuta una secuencia de movimientos en una rutina básica de ejercicios gimnásticos, como rodar, voltear y asumir posiciones estáticas.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 5,
          "name": "Saltos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Saltan desde una pequeña altura y luego caen con ambos pies en forma segura.",
            "Saltan en un pie de forma continua y segura; por ejemplo: dentro de aros que están dispuestos de forma lineal, saltan conos, entre otros.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 6,
          "name": "Patadas",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Patean un objeto que se encuentre en una posición estática.",
            "No Aplica",
            "Patean el balón hacia un compañero con la parte interior y exterior del pie.",
            "Patean un balón hacia un compañero con la parte interna del pie, mientras se desplazan.",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 7,
          "name": "Nociones espaciales y resolución de problemas motrices",
          "levels": [
            "No Aplica",
            "Realiza movimientos, posturas y desplazamientos, siguiendo instrucciones que involucran nociones espaciales como adelante/atrás/al lado/entre, usando como referencia la posición de su cuerpo, en situaciones cotidianas y lúdicas.",
            "Usa conceptos espaciales como adelante/atrás/al lado/entre, teniendo como referencia la posición de los demás o de objetos, en sus descripciones y relatos, durante situaciones cotidianas y juegos.",
            "Usan las nociones espaciales (adelante, atrás, arriba, abajo) en el movimiento y en la interacción con otros.",
            "Resuelven tareas motrices que involucren la solución de un problema de orden y secuencia; por ejemplo: ordenarse de mayor a menor en edad, del más grande al más pequeño en tamaño.",
            "Resuelven problemas motrices con relación al espacio; por ejemplo: logran dar la mayor cantidad de pases posible con un balón en un espacio reducido.",
            "Resuelven tareas motrices dadas por el docente en relación con el tiempo, el espacio y el número de personas.",
            "Solucionan de forma satisfactoria problemas motrices en los que intervienen tres variables: tiempo, espacio y objetos.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 8,
          "name": "Dominio espacial y temporal",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan acciones motrices mediante tareas de dominio espacial y temporal.",
            "Resuelven en forma creativa las situaciones planteadas en los juegos motrices.",
            "",
            "",
            "Resuelven problemas que exijan el dominio de patrones motrices básicos adecuados a diferentes estímulos perceptivos, a las posibilidades del sujeto y la situación de juego.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 9,
          "name": "Representación espacial",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Identifican los contornos o líneas generales del espacio propuesto para la actividad, como la línea central, la pared del fondo o la línea de atrás.",
            "Ejecutan de forma grupal diferentes representaciones, como símbolos, letras, números, palabras o figuras geométricas.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 10,
          "name": "Cálculos en juegos motrices",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Aplican sumas, restas y cálculos simples en juegos motrices diversos; por ejemplo: cuentan los pasos y calculan distancias.",
            "Aplican sumas, restas y multiplicaciones en juegos motrices diversos; por ejemplo: calculan distancias, entre otras.",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 11,
          "name": "Actividades motrices y juegos deportivos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan actividades individuales y colectivas.",
            "Ejecutan actividades junto con sus compañeros.",
            "Ejecutan juegos pre-deportivos colectivos con un máximo de tres reglas.",
            "Ejecutan juegos adaptados en espacios y reglas simples que ponen en práctica algunos fundamentos elementales de deportes individuales y colectivos.",
            "Conocen y ejecutan al menos dos deportes de carácter individual o grupal.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 12,
          "name": "Estrategias de juego",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan juegos grupales y colaborativos; por ejemplo: pillar con un compañero, juegos de relevos, entre otros.",
            "Ejecutan juegos de persecución, de colaboración, individuales o colectivos.",
            "Elaboran y aplican estrategias que impliquen ataque y defensa, buscando objetivos: goles, canastas, ensayos, toques de suelo, etc.",
            "Elaboran actividades que impliquen estrategias de ataque y defensa, buscando objetivos y respetando las reglas señaladas: goles, canastas, ensayos, toques de suelo, etc.",
            "Utilizan estrategias ofensivas y defensivas, teniendo en cuenta que pueden no ser capaces de seleccionar la estrategia más apropiada para la actividad.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 13,
          "name": "Uso del espacio de juego",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Utilizan el terreno de juego y se desplazan en todas las direcciones.",
            "Ejecutan juegos de carácter individual.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 14,
          "name": "Juegos pre-deportivos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan juegos pre-deportivos, utilizando sus principios básicos: seguir la jugada, visión periférica, utilizar las dimensiones del terreno de juego, jugar sin implementos.",
            "Participan en juegos pre-deportivos colectivos, utilizando sus principios básicos: seguir la jugada, visión periférica, utilizar las dimensiones del terreno de juego, jugar con y sin implemento, simpleza.",
            "Ejecutan secuencias de movimientos en actividades deportivas como básquetbol, vóleibol, fútbol, balonmano, natación, entre otros.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 15,
          "name": "Material alternativo",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Crean material alternativo para diferentes actividades, como pelotas de papel o trapo, bastones, entre otros.",
            "Utilizan material alternativo para diferentes actividades, como neumáticos y cuerdas, entre otros.",
            "Utilizan material alternativo para diferentes actividades, como neumáticos, cuerdas, entre otros.",
            "Elaboran y ejecutan un circuito motriz con material alternativo.",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 16,
          "name": "Actividades en entorno natural",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan actividades de trepar en juegos, implementos o árboles.",
            "Utilizan diferentes habilidades motrices de acuerdo a los requerimientos del entorno natural.",
            "Ejecutan actividades en el medio natural, procurando sensibilizar a sus compañeros por el respeto y la conservación del medio natural.",
            "Realizan actividades en el medio natural, procurando sensibilizar a sus compañeros en cuanto al respeto y la conservación del medio natural.",
            "Aplican criterios de conservación del medioambiente en sus actividades escolares; por ejemplo: demuestran cuidar la flora y la fauna en actividades en contacto con la naturaleza y aplican el principio de “no deje rastro”.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 17,
          "name": "Orientación y actividades en la naturaleza",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Utilizan sistemas de referencias (mapas, símbolos, etc.) como guías para ejecutar diferentes actividades físicas en el entorno natural.",
            "Ejecutan actividades físicas orientadas al desarrollo de habilidades motrices en diferentes entornos; por ejemplo: - actividades en la playa: vóley-playa, fútbol, balonmano, juegos populares, etc. - juegos de orientación, utilizando mapas de la escuela o parques cercanos, carreras de orientación, utilización de brújula, etc. - excursiones de un día de duración - realización de una acampada.",
            "Ejecutan actividades orientadas al desarrollo de habilidades motoras: - caminatas en un parque - juegos de orientación, utilizando mapas de la escuela o parques cercanos al centro, y carreras de orientación, utilización de brújula, etc. - excursiones de un día de duración.",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 18,
          "name": "Higiene y cuidado del espacio",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Mantienen limpios los espacios utilizados.",
            "Reconocen medidas de limpieza en los diferentes lugares que hayan utilizado.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 19,
          "name": "Movimiento rítmico y danzas",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan movimientos espontáneos por medio de sonidos o ritmos musicales.",
            "Ejecutan combinaciones simples de movimiento con control del cuerpo a diferentes ritmos.",
            "Utilizan distintos materiales para realizar secuencias de movimientos simples, que impliquen cambios de dirección en diferentes planos según los cambios de ritmos percibidos.",
            "Realizan secuencias de movimientos, procurando llevar el ritmo de una melodía.",
            "Ejecutan una secuencia coreográfica memorizada, siguiendo el ritmo de la música. Por ejemplo: baila una cueca, tamuré o un trote nortino.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 20,
          "name": "Coordinación y expresión corporal",
          "levels": [
            "Explora y comienza a demostrar coordinación entre ojos y pies en juegos simples; por ejemplo, patear una pelota grande hacia adelante, empujar un objeto con el pie o intentar seguir un ritmo con movimientos de pies.",
            "Mantiene la coordinación entre ojos y pies al enfrentar algunos desafíos en sus juegos o actividades cotidianas; por ejemplo, intentar detener con el pie un objeto que rueda por el suelo, patear una pelota, saltar un obstáculo, entre otros.",
            "Mantiene el equilibrio al combinar distintos movimientos, posturas y desplazamientos; por ejemplo, al bailar con cintas siguiendo el ritmo de la música, al correr y patear una pelota, saltar mientras lanzan una pelota, caminar o correr trasladando algún objeto, entre otros.",
            "Ejecutan una secuencia coreográfica memorizada, siguiendo el ritmo de la música. Por ejemplo: baila una cueca, tamuré o un trote nortino.",
            "Expresan sentimientos como alegría, tristeza entre otros, por medio de diferentes movimientos corporales.",
            "Utilizan el movimiento y los gestos como medio de expresión de sentimientos y emociones asociadas a diferentes músicas y sonidos.",
            "Utilizan el movimiento y los gestos como medio de expresión frente a sonidos y ritmos variados.",
            "Demuestran habilidades para expresarse por medio del movimiento.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 21,
          "name": "Danzas tradicionales",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan movimientos en respuesta a diferentes ritmos y músicas.",
            "Ejecutan pasos básicos de danzas tradicionales en forma coordinada.",
            "Ejecutan combinaciones simples de movimientos y pasos de danzas tradicionales en forma coordinada.",
            "Observan, reproducen y experimentan movimientos con diferentes estructuras rítmicas.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 22,
          "name": "Resistencia",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan juegos durante un tiempo determinado para desarrollar la resistencia.",
            "Ejecutan diferentes juegos de carreras intermitentes que permitan el desarrollo de la resistencia.",
            "Desarrollan la resistencia por medio de circuitos.",
            "Realizan circuitos de carácter continuo e intermitente para mejorar la resistencia.",
            "Ejecutan ejercicios físicos para mejorar la resistencia por medio de actividades continuas e intermitentes a diferentes intensidades.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 23,
          "name": "Fuerza",
          "levels": [
            "No Aplica",
            "Empuja y mueve objetos e implementos con ayuda de otros, en situaciones cotidianas o juegos.",
            "Empuja y mueve objetos e implementos de un lugar a otro, de manera individual, en situaciones cotidianas o juegos.",
            "Ejecutan ejercicios en que utilicen el peso de su propio cuerpo para estimular la fuerza.",
            "Utilizan una variedad de grupos musculares en circuitos motrices y juegos en que predomine la fuerza.",
            "Ejecutan una variedad de actividades que activen grupos musculares necesarios para la práctica de actividad física: brazos, piernas, tronco.",
            "Ejecutan actividades de fuerza para grupos musculares, como abdominales, cuádriceps, tríceps y bíceps.",
            "Ejecutan actividades que desarrollan la fuerza, utilizando su propio peso corporal, el de su compañero o implementos que generan sobrecarga.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 24,
          "name": "Flexibilidad",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan movimientos activos de manera individual o con un compañero para mejorar la flexibilidad.",
            "Ejecutan una variedad de ejercicios para mejorar la flexibilidad por medio de movimientos estáticos, pasivos y activos.",
            "Ejecutan carreras en distancias determinadas a máxima velocidad.",
            "Ejecutan ejercicios de corta duración en el menor tiempo posible.",
            "Proponen y ejecutan ejercicios para mejorar la velocidad.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 25,
          "name": "Velocidad",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan carreras en distancias cortas en el menor tiempo posible para mejorar la velocidad de desplazamiento.",
            "Ejecutan desplazamientos a diferentes velocidades y ritmos.",
            "Ejecutan actividades de corta duración que desarrollen la velocidad; por ejemplo: postas y relevos.",
            "Ejecutan actividades de movilidad articular y elongación muscular de tronco y extremidades.",
            "Ejecutan ejercicios para mejorar la fuerza, la flexibilidad, la resistencia y la velocidad.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 26,
          "name": "Habilidades de suspensión",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Se cuelgan de una barra por un periodo determinado.",
            "Se cuelgan de una barra, utilizando diferentes tomadas.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 27,
          "name": "Valoración de la condición física",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Realizan alguna prueba o test para comparar su nivel personal de desarrollo de las cualidades físicas con resultados anteriores.",
            "Ejecutan alguna prueba para valorar el nivel personal de desarrollo de las cualidades físicas y comparan el resultado con el de sus compañeros.",
            "Registran los datos obtenidos en la valoración de la condición física para establecer metas de superación personal.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 28,
          "name": "Participación en actividad física fuera del aula",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Incorporan una variedad de actividades físicas y juegos tradicionales en su vida diaria; por ejemplo: caminar hacia el colegio, jugar a la pinta en el recreo o realizar una caminata al aire libre.",
            "Guiados por el docente, practican una variedad de juegos tradicionales que pueden incorporar en su vida diaria.",
            "Utilizan las dependencias del colegio, plazas y entornos cercanos a sus hogares para practicar ejercicio físico, rutina de actividades, juegos tradicionales y/o deportes, cinco veces a la semana.",
            "Utilizan las dependencias del colegio, plazas y entornos cercanos a sus hogares para practicar ejercicio físico, rutina de actividades, juegos tradicionales y/o deportes, cinco veces a la semana.",
            "Ejecutan de forma individual o grupal actividades físicas o juegos fuera del horario escolar; por ejemplo: andar en bicicleta, caminar o ir en bicicleta al colegio.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 29,
          "name": "Expresión de sensaciones corporales",
          "levels": [
            "OA1. Reconoce qué acciones lo hacen sentir cómodo al utilizar su cuerpo.",
            "Expresa a través de gestos y palabras lo que siente al ejecutar distintos tipos de movimientos, posturas y desplazamientos, incorporando algunos implementos.",
            "Describe lo que siente al realizar movimientos, posturas y desplazamientos, comparando las sensaciones que experimenta al utilizar y no utilizar implementos.",
            "Identifican las distintas respuestas corporales provocadas por la actividad física, como aumento del ritmo de la respiración, sudor y dificultad al hablar.",
            "Describen los cambios observables que experimenta su organismo cuando pasa del estado de reposo al de activación; por ejemplo: aumento de los latidos del corazón y de la temperatura.",
            "Reconocen algunos cambios que sufre el organismo desde un estado pasivo a la realización de actividad física: cambios de latidos, estado de fatiga, sudor.",
            "Describen los cambios que sufre el organismo desde un estado pasivo a la realización de actividad física: sensación de fatiga, grado de tensión muscular y percepción del esfuerzo.",
            "Determinan la intensidad del ejercicio por medio de la frecuencia cardiaca o utilizando una escala de percepción de esfuerzo.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 30,
          "name": "Autocuidado y valores en actividad física",
          "levels": [
            "OA1. Reconoce algunos beneficios de practicar acciones de autocuidado de su cuerpo, en situaciones cotidianas de juego.",
            "Explica los beneficios de practicar acciones de autocuidado de su cuerpo, en situaciones cotidianas y juegos.",
            "Realiza acciones para cuidar su cuerpo que le producen bienestar y placer, en situaciones cotidianas, de manera espontánea.",
            "Reconocen que durante la práctica de actividad física se debe mantener el respeto a los compañeros y el profesor.",
            "Demuestran imparcialidad y respeto cuando participan en un juego o actividad física; por ejemplo: se turnan el uso de los materiales, expresan adecuadamente sus emociones y alientan a sus compañeros.",
            "Aplican los principios de un juego limpio.",
            "Participan colaborativamente con sus compañeros en un juego o actividad deportiva.",
            "Enfrentan los conflictos por medio del diálogo, dando alternativas de solución; por ejemplo: conversan e intentan llegar a acuerdos.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 31,
          "name": "Roles sociales y trabajo en equipo",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Ejecutan juegos en los cuales asumen diferentes roles.",
            "Trabajan cooperativamente para completar un trabajo asignado; por ejemplo: apoyan y ayudan a sus compañeros en una tarea común; cumplen con su parte en las tareas asignadas.",
            "Cumplen las reglas propuestas en el juego.",
            "No Aplica",
            "Indican las faltas cometidas por su equipo después de una actividad.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 32,
          "name": "Iniciativa y responsabilidad en equipo",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Demuestran la iniciativa de querer trabajar en equipo en actividades grupales.",
            "Demuestran solidaridad en situaciones propias de la clase; por ejemplo: ofrecen su ayuda para solucionar situaciones que emergen en la clase; apoyan y ayudan a sus compañeros cuando se caen; trasladan materiales por iniciativa propia.",
            "Preparan el material necesario para realizar una actividad en la naturaleza, asignando funciones a los participantes y velando para que los líderes respondan de manera responsable a las exigencias de la actividad.",
            "Conocen las medidas de precaución que se deben aplicar cuando se practica una actividad en el entorno natural.",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 33,
          "name": "Resolución de conflictos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Enfrentan los conflictos por medio del diálogo, dando alternativas de solución; por ejemplo: conversan e intentan llegar a acuerdos.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 34,
          "name": "Conocimiento corporal y calentamiento",
          "levels": [
            "No Aplica",
            "Describe los cambios que ocurren en su cuerpo durante distintas posturas y movimientos, en juegos y actividades motrices.",
            "Relaciona algunos cambios en su organismo frente a actividades motrices de mayor exigencia (por ejemplo, incremento de los latidos del corazón, ritmo de la respiración, sudoración, entre otros), en situaciones cotidianas y juegos.",
            "Explican con sus palabras la importancia de realizar un calentamiento al inicio de una actividad física.",
            "Ejecutan un calentamiento con un compañero de forma autónoma, para preparar el cuerpo antes de un esfuerzo físico.",
            "En forma autónoma, realizan actividades de calentamiento al inicio de la clase y ejercicios de vuelta a la calma al finalizar la sesión.",
            "Dirigen un calentamiento que incluya todas las fases.",
            "Ejecutan de forma autónoma un calentamiento específico para una actividad física y/o deportiva.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 35,
          "name": "Cumplimiento de normas y seguridad",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Siguen las diferentes reglas para las actividades propuestas.",
            "Se mantienen en los límites establecidos por el docente.",
            "Demuestran conciencia de que las normas de seguridad varían en las diferentes actividades físicas y entornos.",
            "Utilizan aparatos e implementos de manera segura.",
            "Siguen reglas establecidas por el docente para el uso seguro y adecuado de las instalaciones.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 36,
          "name": "Uso y cuidado de implementos",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Utilizan los implementos, como balones, cuerda, aros, bancos, entre otros, bajo la supervisión del profesor.",
            "Cuidan el material de trabajo y lo recogen al término de la clase.",
            "Recogen el material utilizado en la clase para dejar el espacio libre de obstáculos.",
            "Mantienen ordenados los espacios en que realizan las actividades físicas y/o deportivas.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 37,
          "name": "Conductas de seguridad y prevención",
          "levels": [
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "Evitan conductas peligrosas para la propia integridad y la de los pares.",
            "Aplican procedimientos que aumenten su seguridad y la de los demás.",
            "Cuidan sus pertenencias y usan de forma segura los implementos de la clase.",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 38,
          "name": "Autoconcepto corporal",
          "levels": [
            "No Aplica",
            "Describe sus características corporales, mencionando las que más le agradan de sí mismo.",
            "Describe semejanzas y diferencias entre sus características y las de sus pares, reconociendo cuáles de ellas lo hacen único.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 39,
          "name": "Conocimiento anatómico",
          "levels": [
            "OA2. Nombra e identifica las partes principales del cuerpo en situaciones de juego, relacionándolo a una función en situaciones cotidianas o de juegos.",
            "Señala partes más específicas de su cuerpo (por ejemplo, caderas, codos, muñecas, tobillos, talón) y su funcionalidad, en situaciones cotidianas y juegos.",
            "Señala la ubicación y funcionalidad de algunos órganos internos, tales como corazón, pulmones, cerebro, aparato digestivo, entre otros.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 40,
          "name": "Lateralidad",
          "levels": [
            "No Aplica",
            "No Aplica",
            "Describe con qué lado de su cuerpo se siente más cómodo para realizar diversas acciones, tales como saltar en un pie, dibujar o pintar, mover brazos o piernas, entre otras.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 41,
          "name": "Nuevas acciones motrices",
          "levels": [
            "OA3. Reconoce algunas acciones que puede realizar con su cuerpo, en situaciones de juego, respondiendo a las preguntas y estímulos externos.",
            "Describe las nuevas acciones que puede realizar con su cuerpo, en situaciones cotidianas y juegos.",
            "Describe nuevas posibilidades de acción que ha descubierto a partir de movimientos y posturas corporales, incorporando un vocabulario preciso (girar las muñecas, reptar, entre otros).",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 42,
          "name": "Relación acción-objetivo",
          "levels": [
            "No Aplica",
            "Relaciona algunas acciones realizadas con su cuerpo, con objetivos que quiere alcanzar (saltar para esquivar un objeto, correr para llegar más rápido a un lugar, entre otras)",
            "Explica las acciones que debe realizar para ejecutar diversos movimientos y posturas con su cuerpo.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 43,
          "name": "Preferencias de movimiento",
          "levels": [
            "OA4. Realiza actividades de movimiento libre, juegos y actividades, identificando y reconociendo las emociones que podrían sentir.",
            "Comenta qué tipos de movimientos, posturas o desplazamientos prefiere realizar.",
            "Explica de qué manera realizar diversos movimientos, posturas y desplazamientos, en forma habitual, que benefician su bienestar.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
          ],
          "selectedLevelIndex": null
        },
        {
          "id": 44,
          "name": "Coordinación ojo-mano-pie",
          "levels": [
            "OA7. Explora y comienza a demostrar coordinación entre ojos y manos en juegos y actividades cotidianas; por ejemplo, encajar piezas grandes, apilar bloques, enhebrar objetos simples o lanzar una pelota de tamaño adecuado.",
            "Mantiene la coordinación entre ojos y manos al enfrentar algunos desafíos en sus juegos o actividades cotidianas; por ejemplo, trasladar un vaso con agua sin derramarlo o lanzar y recibir una pelota con las manos.",
            "Mantiene el equilibrio al combinar distintos movimientos, posturas y desplazamientos; por ejemplo, al bailar con cintas siguiendo el ritmo de la música, al correr y patear una pelota, saltar mientras lanzan una pelota, entre otros.",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica",
            "No Aplica"
    ],
    "selectedLevelIndex": null
  },
  {
    "id": 45,
    "name": "Patrones motores gruesos",
    "levels": [
      "OA6. Desarrolla patrones motores gruesos que le permitan desplazarse en diferentes direcciones y participar en situaciones cotidianas y de juegos con y sin implementos.",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica",
      "No Aplica"
],
"selectedLevelIndex": null
},
{
  "id": 46,
  "name": "Habilidades propioceptivas",
  "levels": [
    "OA8. Manifiesta habilidades propioceptivas al reconocer, usar y tomar conciencia de su cuerpo (señalar partes del cuerpo, moverse en el espacio, usar su cuerpo para jugar) en situaciones de juego y actividades cotidianas.",
    "No Aplica",
    "No Aplica",
    "No Aplica",
    "No Aplica",
    "No Aplica",
    "No Aplica",
    "No Aplica",
    "No Aplica"
],
"selectedLevelIndex": null
}
      ]
;

// Definición de la dimensión con datos hardcodeados
export const corporalidadMovimientoDimension: Dimension = {
  id: 4,
  name: "Corporalidad y Movimiento",
  skills: corporalidadMovimientoSkills // Datos hardcodeados permanentes
};

export const CorporalidadMovimiento: React.FC<{
  students: Student[];
  selectedStudentIds: (number | string)[];
  handleLevelChange: (studentId: number | string, dimensionId: number, skillId: number, levelIndex: number | null) => void;
  handleNotEvaluatedChange?: (studentId: number | string, dimensionId: number, skillId: number, notEvaluated: boolean) => void;
  viewMode: 'student' | 'skill';
  handleViewModeChange: (mode: 'student' | 'skill') => void;
  showInstructions: boolean;
  selectedCourse: string;
  setSelectedCourse: (course: string) => void;
  courses: string[];
  handleStudentSelect: (event: React.MouseEvent<HTMLButtonElement>, id: number | string) => void;
  getExpectedLevel: (age: number) => number;
  getPreviousYearLevel: (age: number) => number;
}> = ({
  students,
  selectedStudentIds,
  handleLevelChange,
  handleNotEvaluatedChange,
  viewMode,
  handleViewModeChange,
  showInstructions,
  selectedCourse,
  setSelectedCourse,
  courses,
  handleStudentSelect,
  getExpectedLevel,
  getPreviousYearLevel,
}) => {
  const [selectedSkillId, setSelectedSkillId] = useState<number | null>(null);
  const [showAllYears, setShowAllYears] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<"name" | "lastName" | "course">("lastName");

  // useEffect para manejar la selección por defecto
  useEffect(() => {
    if (viewMode === 'student' && selectedStudentIds.length === 0 && students.length > 0) {
      // Usar el primer estudiante disponible del curso actual, no hardcodear
      const firstStudent = students[0];
      if (firstStudent) {
        handleStudentSelect(new MouseEvent('click') as any, firstStudent.id as any);
      }
    } else if (viewMode === 'skill' && !selectedSkillId && corporalidadMovimientoDimension.skills.length > 0) {
      const firstSkill = corporalidadMovimientoDimension.skills[0];
      if (firstSkill) {
        setSelectedSkillId(firstSkill.id);
      }
    }
  }, [viewMode, students, selectedStudentIds, handleStudentSelect]);
  
  // Estado para rastrear niveles previamente seleccionados
  const [previouslySelectedLevels, setPreviouslySelectedLevels] = useState<{[key: string]: number | null}>();

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};

      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === corporalidadMovimientoDimension.id && skillLevel.level !== null && skillLevel.level !== undefined) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });

      setPreviouslySelectedLevels(initialLevels);
    }
  }, [students]);

  const getSkillLevel = (student: Student, dimensionId: number, skillId: number): number | null => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.level ?? null;
  };

  // 🚫 Función para verificar si una habilidad está marcada como "No evaluada"
  const isSkillNotEvaluated = (student: Student, dimensionId: number, skillId: number): boolean => {
    const skillLevel = student.skillLevels.find(
      sl => sl.dimensionId === dimensionId && sl.skillId === skillId
    );
    return skillLevel?.notEvaluated === true;
  };

  // 🎯 NUEVO: Helper compartido para obtener el nivel del año anterior REAL
  const getHistoricalLevel = useCallback((student: Student, skillId: number, currentAge: number) => {
    if (student.previousYearLevels && student.previousYearLevels.length > 0) {
      const historical = student.previousYearLevels.find(
        pl => pl.dimensionId === corporalidadMovimientoDimension.id && pl.skillId === skillId
      );
      if (historical !== undefined && historical.level !== null) {
        return historical.level;
      }
    }
    return getPreviousYearLevel(currentAge);
  }, []);

  const getCompletionColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'bg-indigo-500 text-white border-indigo-600 shadow-md transform scale-105';
    }
    
    if (percentage >= 75) {
      return 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100 hover:border-green-400';
    } else if (percentage >= 50) {
      return 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100 hover:border-blue-400';
    } else if (percentage >= 25) {
      return 'bg-yellow-50 text-yellow-800 border-yellow-300 hover:bg-yellow-100 hover:border-yellow-400';
    } else {
      return 'bg-red-50 text-red-800 border-red-300 hover:bg-red-100 hover:border-red-400';
    }
  };

  const getPercentageTextColor = (percentage: number, isSelected: boolean = false) => {
    if (isSelected) {
      return 'text-white opacity-75';
    }
    
    if (percentage >= 75) {
      return 'text-green-600 opacity-90';
    } else if (percentage >= 50) {
      return 'text-blue-600 opacity-90';
    } else if (percentage >= 25) {
      return 'text-yellow-600 opacity-90';
    } else {
      return 'text-red-600 opacity-90';
    }
  };

  // Función para extraer el primer apellido
  // Formato esperado: "Apellido Paterno; Apellido Materno; Nombre"
  const getFirstLastName = useCallback((fullName: string): string => {
    const nameParts = fullName.trim().split(' ');
    // El primer apellido (apellido paterno) es la primera parte del nombre
    if (nameParts.length >= 1) {
      return nameParts[0].toLowerCase();
    }
    // Si está vacío, retornamos la cadena vacía
    return '';
  }, []);

  const handleLocalLevelChange = useCallback((studentId: number | string, skillId: number, levelIndex: number | null) => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();

    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (student) {
      const currentLevel = getSkillLevel(student, corporalidadMovimientoDimension.id, skillId);

      // Actualizar previouslySelectedLevels si no existe o si cambió
      const currentPrevLevel = previouslySelectedLevels?.[`${studentId}-${skillId}`];
      if (currentLevel !== currentPrevLevel) {
        setPreviouslySelectedLevels(prev => ({
          ...prev,
          [`${studentId}-${skillId}`]: currentLevel
        }));
      }

      // Lógica de toggle: si el nivel ya está seleccionado, deseleccionarlo
      if (currentLevel === levelIndex) {
        handleLevelChange(studentId, corporalidadMovimientoDimension.id, skillId, null);
        return;
      }
    }

    // Si levelIndex es -1, convertir a null (para compatibilidad con el botón de reiniciar)
    const actualLevelIndex = levelIndex === -1 ? null : levelIndex;
    handleLevelChange(studentId, corporalidadMovimientoDimension.id, skillId, actualLevelIndex);
  }, [handleLevelChange, students, previouslySelectedLevels]);

  const filteredStudents = useMemo(() => {
    const filtered = students.filter(student => 
      selectedCourse ? student.course === selectedCourse : true
    );
    
    let sorted = [...filtered];
    
    if (sortBy === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "lastName") {
      sorted.sort((a, b) => {
        const lastNameA = getFirstLastName(a.name);
        const lastNameB = getFirstLastName(b.name);
        return lastNameA.localeCompare(lastNameB);
      });
    } else if (sortBy === "course") {
      sorted.sort((a, b) => a.course.localeCompare(b.course));
    }
    
    if (!searchTerm.trim()) return sorted;
    
    return sorted.filter(student => 
      student.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, selectedCourse, searchTerm, sortBy, getFirstLastName]);

  const renderStudentView = useMemo(() => {
    return () => selectedStudentIds.map(studentId => {
    const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
    const student = students.find(s => normalizeId(s.id) === normalizeId(studentId));
    if (!student) return null;

    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });

    const expectedLevel = getExpectedLevel(age);

    const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
    
    const getVisibleYears = () => {
      if (showAllYears) {
        return allYears;
      }
      
      const studentAge = age;
      const minAge = Math.max(4, studentAge - 2);
      const maxAge = Math.min(12, studentAge + 2);
      
      return allYears.filter((_, index) => {
        const yearAge = index + 4;
        return yearAge >= minAge && yearAge <= maxAge;
      });
    };
    
    const visibleYears = getVisibleYears();
    const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

    return (
      <div key={student.id} className="mb-8">
        <div className="overflow-x-auto">
          <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
            <div className="bg-gray-50 p-3 border-b border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Progreso de habilidades:</span>
                  <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                    {(() => {
                      // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                      const progress = calculateFormProgress(
                        corporalidadMovimientoDimension.id,
                        student.course,
                        student.skillLevels
                      );
                      const progressPercent = progress.percentage;
                      
                      let barColor = "bg-red-500";
                      if (progressPercent >= 75) barColor = "bg-green-500";
                      else if (progressPercent >= 50) barColor = "bg-blue-500";
                      else if (progressPercent >= 25) barColor = "bg-yellow-500";
                      
                      return (
                        <div 
                          className={`h-2.5 rounded-full ${barColor}`} 
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      );
                    })()}
                  </div>
                  <span className="text-xs text-gray-500">
                    {(() => {
                      const progress = calculateFormProgress(
                        corporalidadMovimientoDimension.id,
                        student.course,
                        student.skillLevels
                      );
                      return `${progress.completedSkills} de ${progress.totalValidSkills} habilidades`;
                    })()}
                  </span>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                      showAllYears 
                        ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                        : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                  </button>
                  
                  <button
                    onClick={() => {
                      if (window.confirm("¿Seguro que desea reiniciar todos los niveles de habilidad para este estudiante?")) {
                        corporalidadMovimientoDimension.skills.forEach(skill => {
                          // Pasar un valor para level que será interpretado como null
                          handleLocalLevelChange(student.id as any, skill.id, -1);
                        });
                      }
                    }}
                    className="text-xs text-red-500 hover:text-red-700 font-medium bg-white px-2 py-1 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    Reiniciar
                  </button>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">Habilidad</th>
                    {visibleYears.map((ageGroup, index) => {
                      const actualIndex = visibleIndices[index];
                      return (
                        <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="mb-1">{ageGroup}</span>
                            <button
                              onClick={() => {
                                corporalidadMovimientoDimension.skills.forEach(skill => {
                                  const currentLevel = getSkillLevel(student, corporalidadMovimientoDimension.id, skill.id);
                                  if (currentLevel === actualIndex) {
                                    handleLevelChange(student.id as any, corporalidadMovimientoDimension.id, skill.id, null);
                                  } else {
                                    handleLevelChange(student.id as any, corporalidadMovimientoDimension.id, skill.id, actualIndex);
                                  }
                                });
                              }}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                            >
                              Seleccionar todo
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {corporalidadMovimientoDimension.skills.map((skill: Skill, skillIndex: number) => {
                  const selectedLevel = getSkillLevel(student, corporalidadMovimientoDimension.id, skill.id);
                  const skillIsNotEvaluated = isSkillNotEvaluated(student, corporalidadMovimientoDimension.id, skill.id);
                  
                  const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                  
                  return (
                      <tr key={skill.id} className={`${skillIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-3 px-6 text-sm font-medium text-gray-900">
                          <div className="flex items-start">
                            <span className="rounded-full bg-indigo-100 text-indigo-800 w-6 h-6 flex items-center justify-center mr-2 font-bold text-xs">
                              {skillIndex + 1}
                            </span>
                            <div className="flex-1">
                              {skill.name}
                              {selectedLevel !== null && !skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-green-600 font-medium">
                                  Nivel seleccionado: {selectedLevel + 1}
                                </div>
                              )}
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleNotEvaluatedChange(student.id, corporalidadMovimientoDimension.id, skill.id, !skillIsNotEvaluated)}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                          const level = skill.levels[levelIndex];
                          if (!level) return null;
                          
                          const isNotApplicable = level.toLowerCase().includes('no aplica');
                          const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                          
                          let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                          
                          // 1. Fondo según selección o estado base
                          if (isNotApplicable) {
                            if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.notApplicableSelected;
                            } else {
                              buttonClass += LEVEL_COLORS.notApplicable;
                            }
                          } else if (selectedLevel === levelIndex || (previouslySelected && previouslySelectedLevels?.[`${student.id}-${skill.id}`] === levelIndex)) {
                            buttonClass += LEVEL_COLORS.selected;
                          } else if (previouslySelected) {
                            buttonClass += LEVEL_COLORS.previouslySelected;
                          } else {
                            buttonClass += LEVEL_COLORS.default;
                          }

                          // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                          let extraStyles: React.CSSProperties = {
                            boxSizing: 'border-box'
                          };

                          if (levelIndex === previousYearLevel) {
                            // Aplicar borde gris grueso para nivel año anterior
                            extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                            if (selectedLevel !== levelIndex) {
                              extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                            }
                          }

                          if (levelIndex === expectedLevel) {
                            // Si es esperado, aplicar borde amarillo
                            if (levelIndex !== previousYearLevel) { // Evitar doble borde si es ambos
                              extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                              }
                            }
                          }

                          return (
                            <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                              <button
                                onClick={() => handleLocalLevelChange(student.id as any, skill.id, levelIndex)}
                                className={buttonClass}
                                style={extraStyles}
                                title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${levelIndex === previousYearLevel ? ' (Nivel año anterior)' : ''}${levelIndex === expectedLevel ? ' (Nivel esperado)' : ''}`}
                              >
                                <span className="text-center leading-tight">{level}</span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Controles de navegación rápida */}
            <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
              </div>
              <div className="flex gap-2">
                <button 
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  Volver arriba
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    });
  }, [selectedStudentIds, students, handleLocalLevelChange, getExpectedLevel, getPreviousYearLevel, showAllYears]);

  const renderSkillView = useMemo(() => {
    return () => {
      if (!selectedSkillId) return null;
      
      const skill = corporalidadMovimientoDimension.skills.find(s => s.id === selectedSkillId);
      if (!skill) return null;

    const allYears = Array.from({length: 9}, (_, i) => `Hacia los ${i + 4} años`);
    
    const getVisibleYears = () => {
      if (showAllYears) {
        return allYears;
      }
      
      const averageAge = filteredStudents.length > 0 ? 
        filteredStudents.reduce((sum, student) => {
          const a = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
          return sum + a;
        }, 0) / filteredStudents.length : 6;
      
      const minAge = Math.max(4, Math.floor(averageAge) - 2);
      const maxAge = Math.min(12, Math.floor(averageAge) + 2);
      
      return allYears.filter((_, index) => {
        const yearAge = index + 4;
        return yearAge >= minAge && yearAge <= maxAge;
      });
    };
    
    const visibleYears = getVisibleYears();
    const visibleIndices = visibleYears.map((year, index) => {
      const match = year.match(/\d+/);
      const yearNum = match ? parseInt(match[0]) : 4;
      const calculatedIndex = yearNum - 4;
      // Asegurar que nunca sea NaN y usar fallback con index
      return isNaN(calculatedIndex) ? index : calculatedIndex;
    });

    // Estado masivo de "No evaluada" para la habilidad seleccionada
    const allStudentsNotEvaluated =
      filteredStudents.length > 0 &&
      filteredStudents.every(student =>
        isSkillNotEvaluated(student, corporalidadMovimientoDimension.id, skill.id)
      );

    const toggleNotEvaluatedForAll = () => {
      if (!handleNotEvaluatedChange) return;
      const nextState = !allStudentsNotEvaluated;
      filteredStudents.forEach(student => {
        handleNotEvaluatedChange(
          student.id,
          corporalidadMovimientoDimension.id,
          selectedSkillId,
          nextState
        );
      });
    };

    return (
      <div className="mb-8">
        <div className="overflow-x-auto">
          <div className="bg-white rounded-lg overflow-hidden shadow-lg my-5 border border-gray-200">
            <div className="bg-gray-50 p-3 border-b border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">Progreso de la habilidad:</span>
                  <div className="w-32 h-2.5 bg-gray-200 rounded-full">
                    {(() => {
                      const totalStudents = filteredStudents.length;
                      const completedStudents = filteredStudents.filter(student => 
                        getSkillLevel(student, corporalidadMovimientoDimension.id, skill.id) !== null
                      ).length;
                      const progressPercent = totalStudents > 0 ? (completedStudents / totalStudents) * 100 : 0;
                      
                      let barColor = "bg-red-500";
                      if (progressPercent >= 75) barColor = "bg-green-500";
                      else if (progressPercent >= 50) barColor = "bg-blue-500";
                      else if (progressPercent >= 25) barColor = "bg-yellow-500";
                      
                      return (
                        <div 
                          className={`h-2.5 rounded-full ${barColor}`} 
                          style={{ width: `${progressPercent}%` }}
                        ></div>
                      );
                    })()}
                  </div>
                  <span className="text-xs text-gray-500">
                    {filteredStudents.filter(student => 
                      getSkillLevel(student, corporalidadMovimientoDimension.id, skill.id) !== null
                    ).length} de {filteredStudents.length} estudiantes
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">{skill.name}</span>
                  <button
                    onClick={() => setShowAllYears(!showAllYears)}
                    className={`text-xs font-medium px-3 py-1 border rounded-md transition-colors ${
                      showAllYears 
                        ? 'bg-blue-500 text-white border-blue-500 hover:bg-blue-600' 
                        : 'bg-white text-blue-500 border-blue-500 hover:bg-blue-50'
                    }`}
                  >
                    {showAllYears ? 'Filtrar por edad (±2 años)' : 'Mostrar todos los años (4-12)'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-indigo-700 to-indigo-800 text-white sticky top-0 z-10">
                  <tr>
                    <th className="py-3 px-6 text-left text-xs font-medium uppercase tracking-wider w-1/4">
                      <div className="flex flex-col items-start gap-1">
                        <span>Estudiante</span>
                        {handleNotEvaluatedChange && (
                          <button
                            onClick={toggleNotEvaluatedForAll}
                            className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all border ${
                              allStudentsNotEvaluated
                                ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                : "bg-white text-indigo-900 border-white/50 hover:bg-indigo-50"
                            }`}
                            title="Marcar esta habilidad como no evaluada para todos los estudiantes filtrados"
                          >
                            {allStudentsNotEvaluated
                              ? "✓ Habilitar evaluación"
                              : "🚫 No evaluar esta habilidad"}
                          </button>
                        )}
                      </div>
                    </th>
                    {visibleYears.map((ageGroup, index) => {
                      const actualIndex = visibleIndices[index];
                      return (
                        <th key={`item-${ageGroup}-${index}`} className="py-3 px-4 text-center">
                          <div className="flex flex-col items-center">
                            <span className="mb-1">{ageGroup}</span>
                            <button
                              onClick={() => {
                                filteredStudents.forEach(student => {
                                  const currentLevel = getSkillLevel(student, corporalidadMovimientoDimension.id, selectedSkillId);
                                    if (currentLevel === actualIndex) {
                                      handleLevelChange(student.id as any, corporalidadMovimientoDimension.id, selectedSkillId, null);
                                    } else {
                                      handleLevelChange(student.id as any, corporalidadMovimientoDimension.id, selectedSkillId, actualIndex);
                                    }
                                });
                              }}
                              className="px-2 py-1 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded text-xs font-medium transition-all"
                            >
                              Seleccionar todo
                            </button>
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student, studentIndex) => {
                    const selectedLevel = getSkillLevel(student, corporalidadMovimientoDimension.id, selectedSkillId);
                    const skillIsNotEvaluated = isSkillNotEvaluated(student, corporalidadMovimientoDimension.id, selectedSkillId);
                    
                    const age = deriveAnchoredAge({ course: student.course, birthDate: student.birthDate, preferCourse: true });
                    
                    return (
                      <tr key={student.id} className={`${studentIndex % 2 === 0 ? "bg-gray-50 hover:bg-blue-50" : "hover:bg-blue-50"} ${skillIsNotEvaluated ? "opacity-60" : ""}`}>
                        <td className="py-3 px-6 text-sm font-medium text-gray-900">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-2">
                              <span className="text-indigo-800 font-bold text-xs">{student.name.split(' ')[0][0]}{student.name.split(' ')[1]?.[0]}</span>
                            </div>
                            <div className="ml-1 flex-1">
                              <div className="font-medium">{student.name}</div>
                              <div className="text-xs text-gray-500">{student.course}</div>
                              {selectedLevel !== null && !skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-green-600 font-medium">
                                  Nivel seleccionado: {selectedLevel + 1}
                                </div>
                              )}
                              {skillIsNotEvaluated && (
                                <div className="mt-1 text-xs text-orange-600 font-medium">
                                  🚫 No evaluada
                                </div>
                              )}
                              {/* Botón para marcar como No Evaluada */}
                              {handleNotEvaluatedChange && (
                                <div className="mt-2">
                                  <button
                                    onClick={() => handleNotEvaluatedChange(student.id, corporalidadMovimientoDimension.id, selectedSkillId, !skillIsNotEvaluated)}
                                    className={`text-xs px-2 py-1 rounded border transition-all duration-200 ${
                                      skillIsNotEvaluated
                                        ? "bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200"
                                        : "bg-gray-100 text-gray-600 border-gray-300 hover:bg-gray-200"
                                    }`}
                                    title={skillIsNotEvaluated ? "Habilitar evaluación" : "Marcar como no evaluada (no afectará el cálculo de nivel de logro)"}
                                  >
                                    {skillIsNotEvaluated ? "✓ Habilitar evaluación" : "🚫 No evaluada"}
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {visibleIndices.map((levelIndex: number, mapIndex: number) => {
                          const level = skill.levels[levelIndex];
                          if (!level) return null;
                          
                          const isNotApplicable = level.toLowerCase().includes('no aplica');
                          const previouslySelected = previouslySelectedLevels && previouslySelectedLevels[`${student.id}-${skill.id}`] === levelIndex;
                          
                          let buttonClass = 'p-3 rounded text-sm border transition-all duration-200 min-h-[80px] w-full flex items-center justify-center ';
                          
                          const previousYearLevel = getHistoricalLevel(student, skill.id, age);
                          const expectedLevel = getExpectedLevel(age);

                          const isExpected = levelIndex === expectedLevel;
                          const isPrevious = levelIndex === previousYearLevel;

                          if (isNotApplicable) {
                            if (selectedLevel === levelIndex) {
                              buttonClass += LEVEL_COLORS.notApplicableSelected;
                            } else {
                              buttonClass += LEVEL_COLORS.notApplicable;
                            }
                          } else if (selectedLevel === levelIndex || (previouslySelected && previouslySelectedLevels?.[`${student.id}-${skill.id}`] === levelIndex)) {
                            buttonClass += LEVEL_COLORS.selected;
                          } else if (previouslySelected) {
                            buttonClass += LEVEL_COLORS.previouslySelected;
                          } else {
                            buttonClass += LEVEL_COLORS.default;
                          }

                          // 2. Bordes e indicadores adicionales (pueden coexistir con la selección)
                          let extraStyles: React.CSSProperties = {
                            boxSizing: 'border-box'
                          };

                          if (isPrevious) {
                            // Aplicar borde gris grueso para nivel año anterior
                            extraStyles.border = '4px solid #9CA3AF'; // Gris-400
                            if (selectedLevel !== levelIndex) {
                              extraStyles.backgroundColor = '#F9FAFB'; // Gris-50 si no está seleccionado
                            }
                          }

                          if (isExpected) {
                            // Si es esperado, aplicar borde amarillo
                            if (!isPrevious) {
                              extraStyles.border = '4px solid #FACC15'; // Amarillo-400
                              if (selectedLevel !== levelIndex) {
                                extraStyles.backgroundColor = '#FEFCE8'; // Amarillo-50
                              }
                            }
                          }

                          return (
                            <td key={`student-${student.id}-skill-${skill.id}-level-${levelIndex}-map-${mapIndex}`} className="py-2 px-2 text-center">
                              <button
                                onClick={() => handleLocalLevelChange(student.id as any, selectedSkillId, levelIndex)}
                                className={buttonClass}
                                style={extraStyles}
                                title={isNotApplicable ? "No aplica para este nivel" : `Nivel ${levelIndex + 1}: ${level}${isPrevious ? ' (Nivel año anterior)' : ''}${isExpected ? ' (Nivel esperado)' : ''}`}
                              >
                                <span className="text-center leading-tight">{level}</span>
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {/* Controles de navegación rápida */}
            <div className="bg-gray-50 p-3 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <strong>Tip:</strong> Seleccione un nivel por cada habilidad.
              </div>
              <div className="flex gap-2">
                <button 
                  className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  Volver arriba
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    };
  }, [selectedSkillId, filteredStudents, handleLocalLevelChange, showAllYears, getExpectedLevel, getPreviousYearLevel]);

  const renderSelectionButtons = () => {
    if (viewMode === 'student') {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Estudiante:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar estudiante..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "name" | "lastName" | "course")}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Apellido</option>
                  <option value="course">Curso</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {filteredStudents.length === 0 ? (
              <div className="col-span-full text-center py-4 text-gray-500">
                No se encontraron estudiantes con "{searchTerm}"
              </div>
            ) : (
              filteredStudents.map((student) => {
                const normalizeId = (id: number | string) => String(id ?? '').trim().toUpperCase();
                const isSelected = student.id && selectedStudentIds.some(sel => normalizeId(sel) === normalizeId(student.id));
                // ✅ CORREGIDO: Usar cálculo excluyendo "No aplica"
                const progress = calculateFormProgress(
                  corporalidadMovimientoDimension.id,
                  student.course,
                  student.skillLevels
                );
                const percentage = progress.percentage;
                
                return (
                  <button
                    key={student.id}
                    onClick={(event) => handleStudentSelect(event, student.id as any)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${getCompletionColor(percentage, isSelected)}`}
                  >
                    <div className="font-semibold" title={`${student.name} - ${student.course}`}>
                      {student.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, isSelected)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
                  </button>
                );
              })
            )}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
                <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                  Tip: Ctrl + clic para seleccionar múltiples estudiantes
                </span>
            </div>
          </div>
        </div>
      );
    } else {
      return (
        <div className="mb-6 bg-white p-5 rounded-lg shadow-md border border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
            <h3 className="text-lg font-semibold whitespace-nowrap">Seleccionar Habilidad:</h3>
            
            <div className="flex flex-col md:flex-row justify-center items-center w-full gap-3">
              {/* Barra de búsqueda */}
              <div className="relative w-full md:w-96 mx-auto">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full p-2.5 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500" 
                  placeholder="Buscar habilidad..." 
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>
              
              {/* Selector de ordenamiento */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-600">Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'name' | 'course' | 'lastName')}
                  className="p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="name">Nombre</option>
                  <option value="lastName">Número</option>
                  <option value="course">% Completado</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {corporalidadMovimientoDimension.skills
              .filter(skill => 
                searchTerm === '' || 
                skill.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((skill) => {
                const completed = filteredStudents.filter(student => 
                  getSkillLevel(student, corporalidadMovimientoDimension.id, skill.id) !== null
                ).length;
                const percentage = filteredStudents.length > 0 ? (completed / filteredStudents.length) * 100 : 0;
                
                return (
              <button
                key={skill.id}
                onClick={() => setSelectedSkillId(skill.id)}
                    className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                      getCompletionColor(percentage, selectedSkillId === skill.id)
                }`}
              >
                    <div className="font-semibold" title={skill.name}>
                {skill.name}
                    </div>
                    <div className={`text-xs font-medium ${getPercentageTextColor(percentage, selectedSkillId === skill.id)}`}>
                      {percentage.toFixed(1)}% completado
                    </div>
              </button>
                );
              })}
          </div>
          
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center flex-wrap">
                <p className="text-sm font-medium text-gray-700 mr-2">Leyenda:</p>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-green-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥75% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥50% completado,</span>
                  </span>
                </div>
                <div className="flex items-center mr-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">
                    <span className="font-medium">≥25% completado,</span>
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-red-500 rounded mr-1.5"></div>
                  <span className="text-xs text-gray-600">&lt;25% completado</span>
                </div>
              </div>
                <span className="text-xs text-gray-600 font-medium bg-blue-50 p-1 px-2 rounded border border-blue-200">
                Tip: Selecciona una habilidad para ver detalles
                </span>
            </div>
          </div>
        </div>
      );
    }
  };

  // useEffect para inicializar los niveles previamente seleccionados desde la base de datos
  useEffect(() => {
    if (students.length > 0) {
      // Inicializar los niveles previamente guardados en la base de datos
      const initialLevels: {[key: string]: number} = {};
      
      students.forEach(student => {
        student.skillLevels.forEach(skillLevel => {
          if (skillLevel.dimensionId === corporalidadMovimientoDimension.id && skillLevel.level !== null && skillLevel.level !== undefined) {
            initialLevels[`${student.id}-${skillLevel.skillId}`] = skillLevel.level;
          }
        });
      });
      
      // Solo actualizar si hay datos que inicializar
      if (Object.keys(initialLevels).length > 0) {
        setPreviouslySelectedLevels(initialLevels);
      }
    }
  }, [students]);

      if (corporalidadMovimientoDimension.skills.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando habilidades de Corporalidad y Movimiento...</p>
        </div>
      </div>
    );
  }

  if (corporalidadMovimientoDimension.skills.length === 0) {
    return (
      <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
        <p className="font-bold">Advertencia</p>
        <p>No se pudieron cargar las habilidades desde el archivo CSV. Contacte al administrador.</p>
      </div>
    );
  }

  return (
    <div className={DIMENSION_STYLES.container}>
      <UnifiedHeader
        dimensionName={corporalidadMovimientoDimension.name}
        icon={FaRunning}
        skillsCount={corporalidadMovimientoDimension.skills.length}
        selectedCourse={selectedCourse}
        courses={courses}
        onCourseChange={setSelectedCourse}
      />
        
      {/* Botones de modo de vista */}
      <div className="mt-3">
        <div className="bg-gray-100 p-1 rounded-lg shadow-sm max-w-md">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => handleViewModeChange('student')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'student' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaUser className="mr-1.5 text-sm" />
              <span className="font-medium">Por Estudiante</span>
            </button>
            
            <button
              onClick={() => handleViewModeChange('skill')}
              className={`flex items-center justify-center py-2 px-3.5 rounded-md text-sm transition-all duration-200 ${
                viewMode === 'skill' 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaList className="mr-1.5 text-sm" />
              <span className="font-medium">Por Habilidad</span>
            </button>
          </div>
        </div>
      </div>
      
      {renderSelectionButtons()}
      
      <div className="bg-white p-4 rounded-lg shadow-md">
        {viewMode === 'student' ? renderStudentView() : renderSkillView()}
        
        {/* Leyenda de colores para los niveles de las tablas */}
        {(viewMode === 'student' && selectedStudentIds.length > 0) || (viewMode === 'skill' && selectedSkillId !== null) ? (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🎯</span>
              Leyenda de colores para niveles de desarrollo:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-500 rounded mr-2 shadow-sm border"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel seleccionado</span> - Nivel evaluado para el estudiante
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-yellow-50 border-2 border-yellow-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel esperado</span> - Nivel correspondiente a la edad
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 border-2 border-gray-400 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Nivel año anterior</span> - Nivel del año previo
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Previamente seleccionado</span> - Nivel anterior guardado
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-white border border-gray-200 rounded mr-2 hover:bg-blue-50"></div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">Sin seleccionar</span> - Nivel disponible para evaluar
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-6 h-6 bg-gray-50 rounded mr-2 border flex items-center justify-center">
                  <span className="text-xs text-gray-400 italic">N/A</span>
                </div>
                <span className="text-sm text-gray-600">
                  <span className="font-medium">No aplica</span> - No corresponde para esta edad
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-xs text-gray-500 italic">
                💡 <strong>Tip:</strong> Los colores ayudan a identificar rápidamente el progreso de cada estudiante según su edad y desarrollo esperado.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

